
import React, { createRef, useCallback, useEffect, useState } from "react";
import SVG from "react-inlinesvg";
import { Modal } from "react-bootstrap";
import { formatDate, permissionAllow } from "../../../../../../../../util/Helpers";
import { toAbsoluteUrl } from "../../../../../../../../../_metronic/_helpers";
import cogoToast from "cogo-toast";
import { useGetDocumentQuery, useUpdateDocumentFieldsMutation } from "../../../../../../../../../redux/apiSlice";
import { Permissions, PermissionType } from "../../../../../../../../util/Permissions";
import * as handlers from "../../../../../../_redux/selected-user/selectedUserHandlers";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import AcceptReject from "./accept-reject";
import { useParams } from "react-router-dom";
import MagnifiableImage from "../../../../../../../Common/components/MagnifiableImage";

const ViewDocumentModal = ({ showViewDocumentModal, details, onHide, tableRef }) => {

    const dispatch = useDispatch();
    const { userId } = useParams();
    const [doc, setDoc] = useState(details);
    const adminUser = useSelector(({ auth }) => (auth.user), shallowEqual);
    const [showUpdate, setShowUpdate] = useState(false);
    // RTK Query
    const [updateDocument, { isLoading: isUpdating, isSuccess: updatedSuccessfully, data }] = useUpdateDocumentFieldsMutation();
    const { isfetching: docLoading, isSuccess: getSuccessfully, data: docData } = useGetDocumentQuery(doc);
    const viewRef = createRef();
    const allowUpdate = permissionAllow(adminUser, Permissions.PENDING_KYC, PermissionType.UPDATE);

    // Submit Document field updates
    const updateFields = e => {
        e.preventDefault();
        e.stopPropagation();
        const form = e.currentTarget;

        if (form.checkValidity()) {
            const formData = new FormData(form);
            const docData = { ...doc, ...Object.fromEntries(formData.entries()), meta: doc };
            updateDocument(docData);
        }
    }

    // Refresh Docs Table
    const refreshDocs = useCallback(data => {
        setDoc(doc => ({ ...doc, ...data }))
        if (tableRef) {
            tableRef.current.refresh()
        }
        else if (userId) {
            dispatch(handlers.fetchUserDocuments(userId));
        }
    }, [dispatch, userId, tableRef]);

    // Dpcument fields updated
    useEffect(() => {
        if (updatedSuccessfully) {
            cogoToast.success("Document Fields Updated");
            refreshDocs(data);
            setShowUpdate(false);
        }
    }, [updatedSuccessfully, refreshDocs, data]);

    // Doc Retrieved
    useEffect(_ => {
        if (getSuccessfully) {
            setDoc(doc => ({ ...doc, ...docData }));
        }
    }, [getSuccessfully, docData]);

    return (
        <Modal
            show={showViewDocumentModal}
            onHide={onHide}
            size="xl"
            aria-labelledby="contained-modal-title-vcenter"
        >
            <Modal.Header >
                <Modal.Title id="contained-modal-title-vcenter">
                    <span className={`svg-icon svg-icon-lg svg-icon-primary mr-3`}>
                        <SVG
                            title=""
                            src={toAbsoluteUrl("/media/svg/files/jpg.svg")}
                        />
                    </span>
                    Document Details
                </Modal.Title>
                <button type="button" className="close" onClick={onHide}>
                    <span aria-hidden="true" className="d-block">×</span>
                    <span className="sr-only">Close</span>
                </button>
            </Modal.Header>
            <Modal.Body
                className="px-10 py-2"
                style={{ height: "calc(100vh - 220px)" }}
            >
                <div className="d-flex flex-column h-100">
                    <div className="text-center flex-fill" ref={viewRef}>
                        <div className={"rounded-lg h-100 bg-light-secondary mb-4"}>
                            <MagnifiableImage
                                height={"100%"}
                                width={"100%"}
                                src={doc?.document}
                                alt={doc?.type ?? ""}
                            />
                        </div>
                    </div>

                    {allowUpdate && !docLoading && <AcceptReject doc={doc} topRef={viewRef} refreshDocs={refreshDocs} />}
                    <div className="row">
                        <div className="col-lg-7 py-4">
                            <h6 className=" px">Document Id</h6>
                            <span className="text-muted">{doc?.documentId}</span>
                        </div>
                        <div className="col-lg py-4">
                            <h6 className=" px">Status</h6>
                            <span className="text-muted">{doc?.status ?? "PENDING"}</span>
                        </div>
                    </div>

                    <form onSubmit={updateFields}>
                        <fieldset disabled={!allowUpdate} onInput={_ => setShowUpdate(true)} >
                            <div className="row">
                                <div className="col-lg-4 py-4">
                                    <h6 className=" px">Category</h6>
                                    <select
                                        required=""
                                        name="type"
                                        className="mr-sm-2 custom-select"
                                        defaultValue={doc?.type?.replace(/ /g, "_")?.toUpperCase()}
                                    >
                                        <option value="PASSPORT">PASSPORT</option>
                                        <option value="PICTURE_ID">PICTURE ID</option>
                                        <option value="PROOF_OF_ADDRESS">PROOF OF ADDRESS</option>
                                        <option value="PICTURE_ID_PROOF_OF_ADDRESS">PICTURE ID PROOF OF ADDRESS</option>
                                        <option value="TRUST_DOCUMENTS">TRUST DOCUMENTS</option>
                                        <option value="TAX">TAX</option>
                                        <option value="SIGNATURE">SIGNATURE</option>
                                    </select>
                                </div>
                                <div className="col-lg py-4">
                                    <h6 className=" px">ID Type</h6>
                                    <input
                                        type="text"
                                        defaultValue={doc?.idType}
                                        className="form-control"
                                        name="idType"
                                    />
                                </div>
                                <div className="col-lg-2 py-4">
                                    <h6 className=" px">Side</h6>
                                    <select
                                        required
                                        name="side"
                                        className="mr-sm-2 custom-select"
                                        defaultValue={doc?.side?.toLowerCase()}
                                    >
                                        <option value="front">Front</option>
                                        <option value="back">Back</option>
                                    </select>
                                </div>
                                <div className="col-lg py-4">
                                    <h6 className=" px">ID Value</h6>
                                    <input
                                        type="text"
                                        defaultValue={doc?.idValue ?? ""}
                                        className="form-control"
                                        name="idValue"
                                    />
                                </div>
                            </div>
                            <div className={`text-center py-4 ${showUpdate && allowUpdate ? "" : "d-none"}`}>
                                <button
                                    className='btn btn-dark mb-5 w-75'
                                    type='submit'
                                    disabled={isUpdating}
                                >
                                    Update
                                </button>
                            </div>
                        </fieldset>
                    </form>
                    <div className="row">
                        <div className="col-lg py-4">
                            <h6 className=" px">Created</h6>
                            <span className="text-muted">{formatDate(doc?.created)}</span>
                        </div>
                        <div className="col-lg py-4">
                            <h6 className=" px">Rejection Reason</h6>
                            <span className="text-muted">{doc?.rejectionReason ?? "None"}</span>
                        </div>
                    </div>
                </div>
            </Modal.Body>
            <Modal.Footer className="justify-content-center">
                <button
                    type="button"
                    onClick={onHide}
                    className="btn btn-primary btn-elevate px-5"
                >
                    Close
                </button>
            </Modal.Footer>
        </Modal>

    );
}

export default ViewDocumentModal;